import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Hero } from "@/components/site/Hero";

import heroDesktop from "@/assets/desktop-banner.png.asset.json";
import heroMobileTablet from "@/assets/banner-elvion-tablet.png.asset.json";
import { responsiveImage } from "@/lib/responsive-image";

// Zelfde variant-lijsten als in <Hero /> — zodat de preload exact hetzelfde
// bestand aanvraagt als <picture> uiteindelijk rendert (anders downloadt
// de browser twee keer en helpt de preload de LCP juist niet).
const heroDesktopR = responsiveImage(heroDesktop.url, [960, 1280, 1600, 1920]);
const heroMobileTabletR = responsiveImage(heroMobileTablet.url, [400, 640, 800, 1024, 1280]);

// Below-the-fold: direct imports (geen React.lazy). Lazy + fallback={null}
// veroorzaakte een enorme Cumulative Layout Shift omdat de secties na
// hydratie eerst als leeg renderden en daarna in beeld "poppen". Door ze
// eager te importeren behouden ze exact hun visuele opmaak, maar reserveren
// ze meteen hun ruimte in het document → CLS ≈ 0.
import { ProductRows } from "@/components/site/ProductRows";

import { ContactCta } from "@/components/site/ContactCta";
import { Blog } from "@/components/site/Blog";
import { Careers } from "@/components/site/Careers";
import { ReviewBadge } from "@/components/site/ReviewBadge";
import { Reviews } from "@/components/site/Reviews";
import { REVIEW_BADGE_TONEN } from "@/data/reviews";
import { Newsletter } from "@/components/site/Newsletter";
import { TopBrands } from "@/components/site/TopBrands";
import { Footer } from "@/components/site/Footer";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Elvion Group | Totaalinstallateur e-techniek & beveiliging" },
      {
        name: "description",
        content:
          "Totaalinstallateur voor elektrotechniek, camerabewaking, groepenkasten en laadpalen. Vakkundig, betrouwbaar en actief in heel Nederland.",
      },
      { property: "og:title", content: "Elvion Group | Totaalinstallateur e-techniek & beveiliging" },
      {
        property: "og:description",
        content:
          "Totaalinstallateur voor camerabewaking, groepenkasten, laadpalen en beveiliging in heel Nederland.",
      },

      { property: "og:url", content: "https://elvion-group.nl/" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: heroDesktop.url },
      { name: "twitter:image", content: heroDesktop.url },
    ],
    links: [
      { rel: "canonical", href: "https://elvion-group.nl/" },
      // Preload de LCP-afbeelding per viewport. `imagesrcset`+`imagesizes`
      // laten de browser dezelfde variant kiezen als <picture> straks toont,
      // zodat er geen dubbele download plaatsvindt. href = kleinste variant
      // als fallback voor browsers zonder imagesrcset-support.
      {
        rel: "preload",
        as: "image",
        href: heroDesktopR?.src ?? heroDesktop.url,
        imagesrcset: heroDesktopR?.srcSet ?? heroDesktop.url,
        imagesizes: "50vw",
        media: "(min-width: 1024px)",
        fetchpriority: "high",
        type: "image/webp",
      } as any,
      {
        rel: "preload",
        as: "image",
        href: heroMobileTabletR?.src ?? heroMobileTablet.url,
        imagesrcset: heroMobileTabletR?.srcSet ?? heroMobileTablet.url,
        imagesizes: "100vw",
        media: "(min-width: 640px) and (max-width: 1023.98px)",
        fetchpriority: "high",
        type: "image/webp",
      } as any,
      {
        rel: "preload",
        as: "image",
        href: heroMobileTabletR?.src ?? heroMobileTablet.url,
        imagesrcset: heroMobileTabletR?.srcSet ?? heroMobileTablet.url,
        imagesizes: "100vw",
        media: "(max-width: 639.98px)",
        fetchpriority: "high",
        type: "image/webp",
      } as any,
    ],
  }),

  // Edge-caching voor de SSR-HTML van de homepage. Cloudflare (via
  // CDN-Cache-Control) cachet het gerenderde document 5 min op de POP en
  // mag daarna nog 24 uur "stale" serveren terwijl er in de achtergrond
  // een verse render wordt opgehaald. De browser zelf cachet niet
  // (max-age=0), zodat gebruikers altijd de nieuwste versie zien zodra
  // de edge is ververst. Verlaagt TTFB drastisch zonder layoutwijziging.
  headers: () => ({
    "Cache-Control": "public, max-age=0, must-revalidate",
    "CDN-Cache-Control": "public, s-maxage=300, stale-while-revalidate=86400",
  }),

  component: Index,
});


function Index() {
  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <Header variant="shop" />
      <main>
        {/* Eén Hero: bevat een <picture> die de juiste bron per viewport kiest,
            zodat de browser exact één LCP-afbeelding downloadt. */}
        <Hero />

        {/* De badge blijft gemonteerd (cijfers, links en synchronisatie blijven
            actief), maar is op desktop, tablet en mobiel niet zichtbaar.
            Zet REVIEW_BADGE_TONEN in src/data/reviews.ts op true om hem te tonen. */}
        <div
          className={REVIEW_BADGE_TONEN ? "contents" : "hidden"}
          aria-hidden={!REVIEW_BADGE_TONEN}
        >
          <ReviewBadge />
        </div>
        <ProductRows />
        <ContactCta />
        <Reviews />
        <Blog />
        <Careers />
        <Newsletter />
        <TopBrands />
      </main>
      <Footer />
    </div>
  );
}
